import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Package, Loader, AlertCircle, HelpCircle, Gem, ChevronDown, ChevronUp } from 'lucide-react'
import { motion } from 'motion/react'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import HelpModal from '../components/HelpModal'
import useAudioManager from '../hooks/useAudioManager'

const MAX_VISIBLE_CATEGORIES = 6

const getCaseCategories = (caseItem) => {
  if (!Array.isArray(caseItem.category)) {
    return []
  }

  return caseItem.category.filter(
    (category) => typeof category === 'string' && category.trim() !== ''
  )
}

const sortCategories = (categories, categoryOrder = []) => {
  const uniqueCategories = Array.from(new Set(categories))

  if (!Array.isArray(categoryOrder) || categoryOrder.length === 0) {
    const hasLimited = uniqueCategories.includes('Limited')
    const sortedCategories = uniqueCategories
      .filter((category) => category !== 'Limited')
      .sort((a, b) => a.localeCompare(b, 'ru'))

    return hasLimited ? ['Limited', ...sortedCategories] : sortedCategories
  }

  const categoryOrderMap = new Map(
    categoryOrder.map((category, index) => [category, index])
  )

  const orderedCategories = uniqueCategories
    .filter((category) => categoryOrderMap.has(category))
    .sort((a, b) => categoryOrderMap.get(a) - categoryOrderMap.get(b))

  const unorderedCategories = uniqueCategories
    .filter((category) => !categoryOrderMap.has(category))
    .sort((a, b) => a.localeCompare(b, 'ru'))

  return [...orderedCategories, ...unorderedCategories]
}

const Cases = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { cases, categoryOrder, loadCases, isLoading, error } = useCasesStore()
  const { balance, user } = useAppStore()
  const { playSound } = useAudioManager({
    hover: 'hover',
    pick: 'pick',
    select: 'select'
  })

  // Состояние для активной категории (запоминаем выбор пользователя)
  const [activeCategory, setActiveCategory] = useState(() => {
    return localStorage.getItem('selectedCaseCategory') || 'All'
  })
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [shiningCaseId, setShiningCaseId] = useState(null)

  // Восстанавливаем позицию скролла при возврате на страницу
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('casesScrollPosition')
    if (savedScrollPosition && cases.length > 0) {
      // Небольшая задержка для корректного восстановления после рендера
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10))
      }, 0)
    }
  }, [cases.length])

  // Сохраняем позицию скролла при уходе со страницы
  useEffect(() => {
    const handleScroll = () => {
      sessionStorage.setItem('casesScrollPosition', window.scrollY.toString())
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    // Загружаем кейсы только если они еще не загружены
    if (cases.length === 0 && !isLoading && !error) {
      loadCases()
    }
  }, [cases.length, isLoading, error, loadCases])

  // Сохраняем выбранную категорию в localStorage
  useEffect(() => {
    localStorage.setItem('selectedCaseCategory', activeCategory)
  }, [activeCategory])

  const handleCaseClick = (caseId) => {
    playSound('select')
    navigate(`/cases/${caseId}`)
  }

  const handleCaseHover = () => {
    playSound('hover')
  }

  const categorizedCases = useMemo(() => {
    const filteredCases = cases
      .filter((caseItem) => caseItem.name !== 'Welcome Case')
      .map((caseItem) => ({
        ...caseItem,
        categories: getCaseCategories(caseItem)
      }))
      .filter((caseItem) => caseItem.categories.length > 0)

    const casesByCategory = new Map()

    filteredCases.forEach((caseItem) => {
      caseItem.categories.forEach((category) => {
        if (!casesByCategory.has(category)) {
          casesByCategory.set(category, [])
        }

        casesByCategory.get(category).push(caseItem)
      })
    })

    return sortCategories(Array.from(casesByCategory.keys()), categoryOrder).map((category) => ({
      category,
      cases: [...casesByCategory.get(category)].sort(
        (a, b) => Number(a.price) - Number(b.price)
      )
    }))
  }, [cases, categoryOrder])

  const availableCategories = useMemo(() => {
    return ['All', ...categorizedCases.map(({ category }) => category)]
  }, [categorizedCases])

  const hasHiddenCategories = availableCategories.length > MAX_VISIBLE_CATEGORIES

  const visibleCategoryTabs = useMemo(() => {
    if (showAllCategories || !hasHiddenCategories) {
      return availableCategories
    }

    const defaultVisibleCategories = availableCategories.slice(0, MAX_VISIBLE_CATEGORIES)

    if (activeCategory && availableCategories.includes(activeCategory) && !defaultVisibleCategories.includes(activeCategory)) {
      return [...defaultVisibleCategories, activeCategory]
    }

    return defaultVisibleCategories
  }, [activeCategory, availableCategories, hasHiddenCategories, showAllCategories])

  const visibleSections = useMemo(() => {
    if (activeCategory === 'All') {
      return categorizedCases
    }

    return categorizedCases.filter(({ category }) => category === activeCategory)
  }, [categorizedCases, activeCategory])

  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(activeCategory)) {
      setActiveCategory('All')
    }
  }, [availableCategories, activeCategory])

  const getCategoryLabel = (category) => {
    if (category === 'All' || category === 'Limited') {
      return t(`cases.categories.${category}`)
    }

    return category
  }

  const [showHelp, setShowHelp] = useState(false)

  return (
    <div className="min-h-screen bg-dark-primary text-white pb-6">
      {/* Sticky Balance Header - прилипает при скролле */}
      <div className="sticky top-0 z-10 bg-dark-primary/95 backdrop-blur-sm border-b border-white/10 px-6 py-3 mb-6 lg:border-none">
        <div className="flex items-center justify-between lg:justify-center">
          <h1 className="text-2xl font-bold text-accent-primary flex items-center gap-2 lg:text-4xl">
            <Package className="w-6 h-6 lg:w-8 lg:h-8" /> {t('cases.title')}
            <button
              onClick={() => setShowHelp(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </h1>
          <button
            onClick={() => navigate('/payment')}
            className="flex items-center gap-2 bg-surface px-4 py-2 rounded-lg border border-accent-primary/30 hover:border-accent-primary transition-all cursor-pointer hover:scale-105 lg:hidden"
          >
            <Gem size={18} className="text-accent-primary" />
            <span className="font-bold text-accent-primary">{balance.toLocaleString()}</span>
          </button>
        </div>
      </div>

      {/* Content Container */}
      <div className="px-6">

      {/* Табы категорий */}
      {!isLoading && !error && availableCategories.length > 0 && (
        <div className="mb-6">
          <div className="flex gap-2 flex-wrap justify-center">
            {visibleCategoryTabs.map((category) => {
              const isActive = activeCategory === category
              const isLimitedTab = category === 'Limited'
              
              return (
                <button
                  key={category}
                  onClick={() => {
                    playSound('pick')
                    setActiveCategory(category)
                  }}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-accent-primary text-dark-primary'
                      : isLimitedTab
                        ? 'bg-gradient-to-r from-emerald-400/20 via-cyan-400/15 to-violet-500/20 text-emerald-200 '
                        : 'bg-surface text-text-secondary hover:bg-surface/80'
                  }`}
                >
                  {getCategoryLabel(category)}
                </button>
              )
            })}

            {hasHiddenCategories && (
              <button
                onClick={() => {
                  playSound('pick')
                  setShowAllCategories((previousValue) => !previousValue)
                }}
                className="flex items-center justify-center rounded-lg border border-white/10 bg-surface px-4 py-2 text-text-secondary transition-colors hover:bg-surface/80 hover:text-white"
              >
                {showAllCategories ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loader className="animate-spin text-accent-primary" size={48} />
        </div>
      ) : error ? (
        /* Error State */
        <div className="text-center py-12">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('common.error')}
          </h3>
          <p className="text-text-secondary mb-4">
            {error}
          </p>
          <button 
            onClick={() => loadCases()}
            className="bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition-all"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {visibleSections.map(({ category, cases: categoryCases }) => (
            <section key={category}>
              <h2 className="mt-12 mb-6 text-center text-3xl font-black  text-accent-primary lg:mb-8 lg:text-5xl">
                {getCategoryLabel(category)}
              </h2>

              <div className="flex flex-wrap justify-center gap-3">
                {categoryCases.map((caseItem) => (
                  <div
                    key={caseItem.id}
                    onClick={() => handleCaseClick(caseItem.id)}
                    onMouseEnter={() => {
                      handleCaseHover()
                      setShiningCaseId(caseItem.id)
                    }}
                    onMouseLeave={() => {
                      setShiningCaseId((currentCaseId) => currentCaseId === caseItem.id ? null : currentCaseId)
                    }}
                    className="group w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-3rem)/5)] bg-surface rounded-lg border border-white border-opacity-10 overflow-hidden cursor-pointer transition-colors duration-300 ease-out hover:border-accent-primary/50"
                  >
                    <div className="relative aspect-square bg-gradient-to-br from-dark-primary to-surface flex items-center justify-center overflow-hidden">
                      {shiningCaseId === caseItem.id && (
                        <motion.div
                          key={caseItem.id}
                          className="pointer-events-none absolute inset-y-[-10%] left-[-45%] z-10 w-[60%] rotate-[18deg] mix-blend-screen"
                          style={{
                            background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.02) 25%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.02) 75%, rgba(255,255,255,0) 100%)',
                            boxShadow: '0 0 16px rgba(255, 255, 255, 0.08)'
                          }}
                          initial={{ x: '-60%', opacity: 1 }}
                          animate={{ x: '320%', opacity: [1, 1, 0] }}
                          transition={{ duration: 0.3, ease: 'linear' }}
                          onAnimationComplete={() => {
                            setShiningCaseId((currentCaseId) => currentCaseId === caseItem.id ? null : currentCaseId)
                          }}
                        />
                      )}
                      <img 
                        src={caseItem.img} 
                        alt={caseItem.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-110"
                      />
                    </div>

                    <div className="p-3">
                      <h3 className="text-sm font-bold text-white mb-2 leading-tight">
                        {caseItem.name}
                      </h3>
                      
                      <div className={`flex items-center gap-1 font-bold text-sm ${
                        user?.balance <= caseItem.price ? 'text-text-secondary' : 'text-accent-primary'
                      }`}>
                        <Gem size={14} />
                        <span>{Number(caseItem.price).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Empty State - показываем если нет кейсов в выбранной категории */}
      {!isLoading && !error && visibleSections.length === 0 && (
        <div className="text-center py-12">
          <Package size={64} className="mx-auto text-text-secondary mb-4" />
          <h3 className="text-xl font-semibold text-text-secondary mb-2">
            {t('cases.noCases')}
          </h3>
          <p className="text-text-secondary">
            {t('cases.noCasesDescription')}
          </p>
        </div>
      )}
      
      </div>
      {/* End Content Container */}

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('cases.helpTitle')}
        steps={[
          t('cases.helpStep1'),
          t('cases.helpStep2'),
          t('cases.helpStep3'),
          t('cases.helpStep4')
        ]}
        buttonText={t('cases.helpGotIt')}
      />
    </div>
  )
}

export default Cases
